import * as dataLayerConstants from '../../analytics/src/datalayer/constants';
import getUrlParameterByName from './helpers/get-url-parameter-by-name';
import addUrlParameterToUrl from './helpers/add-url-parameter-to-url';
import removeParamsFromUrl from './helpers/remove-params-from-url';
import campaignOverlayInit from './overlays/index';
import initContentReadingTracking from '../../analytics/src/datalayer-content-reading-tracking';
import headerButtonLoaded from './message-handlers/header-button-loaded';
import sublinksLoaded from './message-handlers/sublinksLoaded';
import payWallEndBannerDisplayed from './message-handlers/pay-wall-end-banner-displayed';
import inlinePayWallDisplayed from './message-handlers/inline-pay-wall-displayed';
import payWallDisplayed from './message-handlers/pay-wall-displayed';
import regWallDisplayed from './message-handlers/reg-wall-displayed';

const PianoCustomEvents = {};

function dismissRegWall () {
	const docRoot = document.querySelector( '.article__content' );

	if ( window.articleSnapshot ) {
		docRoot.innerHTML = window.articleSnapshot; // phpcs:ignore WordPressVIPMinimum.JS.InnerHTML.Found
	}

	const banner = docRoot.querySelector( '.tp-container-inner' );

	if ( banner ) {
		const parentElement = banner.parentElement;
		parentElement.removeChild( banner );
	}

	const modal = document.querySelector( '.tp-modal' );
	const closeButton = document.querySelector( '.tp-close' );
	if ( modal && closeButton ) {
		closeButton.click();
		initContentReadingTracking();
	}

	googletag.pubads().refresh();
}

PianoCustomEvents.processingRegWallEvent = false;

PianoCustomEvents.handlers = {
	'missing-issue': params => {
		window.open( window.QSS_ROUTE_MISSING_ISSUE, '_blank' );
	},
	'need-help': params => {
		window.open( window.QSS_ROUTE_NEED_HELP, '_blank' );
	},
	'reg-wall-sign-up-piano': params => {
		if ( typeof dataLayer !== 'undefined' ) {
			dataLayer.push( {
				event: 'regwall',
				event_action: 'click',
				event_object_name: 'register button',
				event_object_text: params.objectText,
				event_label: params.gaLabel,
				piano_label: params.gaLabel,
			} );
		}
	},
	'cta-button-clicked': params => {
		if ( dataLayer ) {
			dataLayer.push( {
				event: 'regwall',
				event_action: 'click',
				event_object_name: params.objectName,
				event_object_text: params.objectText,
				event_label: params.gaLabel,
				piano_label: params.gaLabel,
			} );
		}
	},
	'cta-close-clicked': params => {
		if ( dataLayer ) {
			dataLayer.push( {
				event: 'regwall',
				event_action: 'click',
				event_object_name: 'close button',
				event_object_text: '',
				event_label: params.gaLabel,
				piano_label: params.gaLabel,
			} );
		}
		dismissRegWall();
	},
};

PianoCustomEvents.messageProcessors = {
	piano_pageview: messageParts => {
		const payload = messageParts[ 1 ];
		if ( ! payload ) {
			return;
		}

		let pageviewData = {};
		try {
			pageviewData = JSON.parse( payload );
		} catch ( error ) {
			return;
		}

		if ( typeof dataLayer !== 'undefined' ) {
			dataLayer.push( {
				event: 'piano_pageview',
				piano_label_piano_pageview: pageviewData?.[ 'piano-label' ] ?? '',
			} );
		}
	},
	'header-button-loaded': messageParts => {
		headerButtonLoaded( messageParts );
	},
	'sublinks-loaded': messageParts => {
		sublinksLoaded( messageParts );
	},
	'campaign-overlay-loaded': messageParts => {
		const campaignData = JSON.parse( messageParts[ 1 ] );
		campaignOverlayInit( campaignData );
	},
	'reg-wall-dismiss-clicked': () => {
		if ( typeof dataLayer !== 'undefined' ) {
			dataLayer.push( {
				event: 'regwall',
				event_action: 'click',
				event_object_name: 'close button',
				event_object_text: '',
			} );
			dismissRegWall();
		}
	},
	'pay-wall-end-banner-displayed': messageParts => {
		payWallEndBannerDisplayed( messageParts );
	},
	'pay-wall-clicked': messageParts => {
		if ( dataLayer ) {
			dataLayer.push( {
				event: 'paywall',
				event_action: 'click',
				event_label: messageParts[ 1 ],
				event_type: messageParts[ 5 ],
				event_object_name: messageParts[ 3 ],
				event_object_text: messageParts[ 4 ],
				service_platform: dataLayerConstants.SERVICE_PLATFORM_PIANO,
				piano_label: messageParts[ 1 ],
			} );
		}

		if ( 'subscribe' === messageParts[ 5 ] ) {
			if ( 'RTA' in window ) {
				window.RTA.pageEvent( { paywall_signup_article: 1 } );
				window.RTA.defer( 'paywall' );
			}
		}
	},
	'inline-pay-wall-displayed': messageParts => {
		let intervalAttempts = 0;
		const maxIntervalAttempts = 10;

		const paywallReady = setInterval( () => {
			intervalAttempts ++;
			const paywallElement = document.querySelector( '#subscription-barrier iframe' );
			if ( paywallElement ) {
				clearInterval( paywallReady );
				inlinePayWallDisplayed( messageParts );
			}

			if ( intervalAttempts >= maxIntervalAttempts ) {
				clearInterval( paywallReady );
			}
		}, 300 );
	},
	'pay-wall-displayed': messageParts => {
		payWallDisplayed( messageParts );
	},
	'reg-wall-displayed': messageParts => {
		regWallDisplayed( messageParts );
	},
	'ad-refresh': messageParts => {
		// Refresh the ads on the page
		googletag.pubads().refresh();
	},
	'cta-email-receipt-clicked': () => {
		if ( dataLayer ) {
			dataLayer.push( {
				event: 'email_receipt',
				service_platform: 'Piano',
				event_category: 'account management',
				event_action: 'email receipt',
				event_type: 'step 1',
			} );
		}
	},
};

PianoCustomEvents.messageHandler = function ( event ) {
	try {
		const allowedOrigins = ( window.PIANO_MESSAGE_TARGETS || [
			{ origin: window.PIANO_MESSAGE_ORIGIN },
			{ origin: window.PIANO_ID_MESSAGE_ORIGIN },
		] )
			.map( ( { origin } ) => new URL( origin, window.location.href ).origin ) // phpcs:ignore WordPressVIPMinimum.JS.Window.location
			.filter( Boolean );

		if (
			! allowedOrigins.includes( event.origin ) ||
			typeof event.data !== 'string' ||
			event.data.indexOf( '|' ) === - 1
		) {
			// Don't handle this message; either malformed, or not from expected origin.
			return;
		}
	} catch ( e ) {
		// Misconfigured allowed origin URL(s); don't handle messages.
		return;
	}
	const messageParts = event.data.split( '|' );


	if ( typeof PianoCustomEvents.messageProcessors[ messageParts[ 0 ] ] === 'function' ) {
		PianoCustomEvents.messageProcessors[ messageParts[ 0 ] ]( messageParts );
	}
};

export default PianoCustomEvents;
